// PermissionsContext.jsx
// Provides permission checking across the app

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './axiosConfig';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      permissions: null,
      loading: true,
      error: null,
      hasPermission: () => false,
      hasAnyPermission: () => false,
      hasAllPermissions: () => false,
      isOwner: () => false,
      isManager: () => false,
      refreshPermissions: () => {},
    };
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPermissions = useCallback(async () => {
    // Don't load if not authenticated
    if (!isAuthenticated || !user) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/permissions/my-permissions');
      setPermissions(response.data.permissions);
      setError(null);
    } catch (err) {
      console.error('Failed to load permissions:', err);
      
      // If 401/403, user may not be properly authenticated
      if (err.response?.status === 401 || err.response?.status === 403) {
        setPermissions(null);
      } else {
        // For other errors, give owners full access, others restricted
        const userRole = user?.role?.toUpperCase();
        if (userRole === 'ACCOUNT_OWNER') {
          setPermissions({ is_owner: true });
        } else {
          setPermissions(null);
        }
      }
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionKey) => {
    // If still loading, assume no permission (will re-render when done)
    if (loading || !permissions) return false;
    
    // Owner has all permissions
    if (permissions.is_owner) return true;
    
    return permissions[permissionKey] === true;
  }, [permissions, loading]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissionKeys) => {
    if (loading || !permissions) return false;
    if (permissions.is_owner) return true;
    
    return permissionKeys.some(key => permissions[key] === true);
  }, [permissions, loading]);

  // Check if user has all of the specified permissions
  const hasAllPermissions = useCallback((permissionKeys) => {
    if (loading || !permissions) return false;
    if (permissions.is_owner) return true;
    
    return permissionKeys.every(key => permissions[key] === true);
  }, [permissions, loading]);

  // Check if user is owner
  const isOwner = useCallback(() => {
    if (loading || !permissions) return false;
    return permissions?.is_owner === true;
  }, [permissions, loading]);
  
  const isManager = useCallback(() => {
    if (loading || !permissions) return false;
    return permissions?.is_manager === true || permissions?.role === 'ACCOUNT_MANAGER';
  }, [permissions, loading]);

  // Refresh permissions (useful after login or role changes)
  const refreshPermissions = useCallback(() => {
    loadPermissions();
  }, [loadPermissions]);

  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isOwner,
    isManager,
    refreshPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

// HOC for wrapping components that need permission checks
export const withPermission = (WrappedComponent, requiredPermission) => {
  return function PermissionWrapper(props) {
    const { hasPermission, loading } = usePermissions();
    
    if (loading) {
      return <div>Loading...</div>;
    }
    
    if (!hasPermission(requiredPermission)) {
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#64748b',
        }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this section.</p>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};

// Simple component that only renders children if permission is granted
export const PermissionGate = ({ permission, children, fallback = null }) => {
  const { hasPermission, loading } = usePermissions();
  
  if (loading) return null;
  
  if (!hasPermission(permission)) {
    return fallback;
  }
  
  return children;
};

export default PermissionsContext;